define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/store/Memory",
	"dgrid/Grid",
	"csi/_base/underscore",
	"csi/form/Widget",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/xml",
	"../SizeChart"
], function(declare, lang, Memory, Grid, _, FormWidget, html, locale, reflection, xml, SizeChart) {

	return declare(FormWidget, {
		ADIM: "SizeChartPasteDimensionAttr",
		DATA: "SizeChartPasteData",
		NDIM: "SizeChartPasteDimensionIndex",
		UNITS: "SizeChartPasteUnits",

		bWriteVisible: false,
		letterRegExp: new RegExp("\\w"),
		digitRegExp: new RegExp("\\d"),

		_dummyStore: new Memory({data: []}),

		// Memory: stores for selecting indices in pasted data
		_dimStore: null,
		_nameStore: null,

		// DOMNode: table showing data
		_tableNode: null,

		// remember parsed data rows to add to server call
		_dataRows: null,

		_createStoresForIndexFields: function(/*Map*/colsByIndex) {
			// Called by _onWatchData() to create stores for index-type fields
			this._dimStore = this._createStoreForIndexField(this.NDIM, colsByIndex, true);
		},

		_createStoreForIndexField: function(/*String*/fieldName, /*Map*/colsByIndex, /*Boolean*/requiredField) {
			// create store for index-type field
			var value = Number(this.getValueForField(this.defView.getField(fieldName)) || (requiredField ? 0 : -1));
			value = String(Math.min(value, colsByIndex.length - 1));

			return new Memory({
				data: (requiredField) ? colsByIndex : [{id: "-1", name: ""}].concat(colsByIndex),
				getEmptyValue: function() {return value;}
			});
		},

		getFieldStore: function(/*Field*/field, /*String*/value) {
			// summary: return stores for fields
			switch (field.m_name) {
				case this.ADIM: return this._getAttributeStore("ApparelDimension");
				case this.NDIM: return this._dimStore || this._dummyStore;
				case this.UNITS: return this._getUnitsStore();
			}

			return null;
		},

		_getAttributeStore: function(/*String*/type) {
			return new Memory({
				data: _.chain(reflection.getClass(type).getAttributes(false, true))
					.filter(function(rca) {return (rca.getType() == xml.ATTR_STRING) && rca.isEnabled && !rca.isHidden})
					.map(function(rca) {return {id: rca.getId(), name: locale.typeId(type, rca.getId())};})
					.sortBy(function(obj) {return obj.id;})
					.value()
			});
		},

		_getUnitsStore: function() {
			return new Memory({
				data: [
					{id: "0", name: locale.resource("Size Chart Units:CM")},
					{id: "1", name: locale.resource("Size Chart Units:MM")},
					{id: "2", name: locale.resource("Size Chart Units:IN - Decimal")}
				]
			});
		},

		getFormContext: function() {
			// summary: add attributes to support _hideExpressions
			var ctx = this.inherited(arguments);
			var node = ctx.getNode();
			xml.nodeCreateClientAttr(node, this.DATA, xml.ATTR_STRING, this.defView.getFieldValue(this.DATA));
			return ctx;
		},

		_postView: function() {
			// summary: only allow Ctrl+V to paste data and watch for when it happens
			var w = this.defView.getFieldControl(this.DATA);

			w.onInput = function(evt) {
				return html.ctrlKeyPressed(evt) && (!evt.charOrCode || (evt.charOrCode == "V"));
			};

			w.on("paste", lang.hitch(this, this._onWatchData));

			this.inherited(arguments);
		},

		_getUnitsDefaultValue: function() {
			var defaultValue = "0"; // CM
			var defaultAdminValue;

			if (xml.enumGetText(SizeChart.valueUnits)) {
				defaultAdminValue = SizeChart.valueUnits;
			}

			if (defaultAdminValue) {
				var unitsFieldStoreValue = this._getUnitsStore().query({name: locale.resource(defaultAdminValue)});
				defaultValue = unitsFieldStoreValue.length > 0 ? unitsFieldStoreValue[0].id : "1";
			}

			return defaultValue;
		},

		/**
		 * parse data to see if it is valid - if so, then convert to HTML table and display other fields
		 * @param {Event} e - paste event
		 */
		_onWatchData: function(e) {
			const newValue = e.clipboardData.getData('text')
			if (!newValue) return;

			try {
				this._dataRows = this._parseData(newValue);

				// create stores for dimension and name indices
				var cols = this._dataRows[0];
				var colsByIndex = _.map(cols, function(col, i) {return {id: String(i), name: col};});

				this._createStoresForIndexFields(colsByIndex);

				// render HTML to show data
				var columns = _.map(cols, function(col, i) {return {label: col, field: i, sortable: false};});
				var commentNode = this.bodyNode.firstChild.firstChild;

				html.clear(commentNode);

				var grid = new Grid({
					"class": "formPasteGrid",
					columns: columns
				}, html.create("div", null, commentNode));

				grid.renderArray(this._dataRows.slice(1));

				// set the field value to trigger change to visibility of other fields
				this.invalidNode.set("value", "");
				this.setFieldValue(this.DATA, newValue);
			} catch (ex) {
				// show error for given input
				this.invalidNode.set("value", ex.message);
				this.setFieldValue(this.DATA, "");
			}
		},

		_parseData: function(/*String*/data) {
			// summary: parse and validate entered data against size chart
			var err = function(s) {throw new Error(locale.resource(s));};

			// parse data into rows, skipping empty rows
			var rows = data.split("\n").filter((s) => !!s.trim());

			// check for at least 2 rows
			if (rows.length < 2) {
				err("SizeChartPasteErrorRows");
			}

			// parse rows into columns, first compressing multiple blanks into single blanks
			var separator = this._inferSeparator(rows);
			rows = _.map(rows, function(row) {return row.replace(/[ ]+/g, " ").split(separator);});

			// check that all rows have same number of columns
			var numCols = rows[0].length;
			if (_.some(rows, function(cols) {return cols.length != numCols;})) {
				err("SizeChartPasteErrorRowsAndColumns");
			}

			return rows;
		},

		_inferSeparator: function(/*String[]*/rows) {
			// summary: figure out what character is being used as the value separator
			var err = function(s) {throw new Error(locale.resource(s));};

			var headingsRow = rows[0];
			var dataRow1 = rows[1];
			var candidates = this._findCandidateSeparators(headingsRow);
			candidates = this._intersect(candidates, this._findCandidateSeparators(dataRow1));

			var bestCandidate = null;
			var maxSplitSize = 0;
			for (var candidate in candidates) {
				var headings = headingsRow.split(candidate);
				var data1 = dataRow1.split(candidate);
				if (headings.length == data1.length && headings.length > maxSplitSize) {
					bestCandidate = candidate;
					maxSplitSize = headings.length;
				}
			}

			if (bestCandidate) {
				return bestCandidate;
			} else {
				err("SizeChartPasteErrorNoClearSeparator");
			}
		},

		_findCandidateSeparators: function(/*String*/row) {
			// summary: return the set of non-alphanumeric characters in row
			var candidates = {};
			for (var i = 0; i < row.length; i++) {
				var ch = row.charAt(i);
				if (!this._isLetterOrDigit(ch)) {
					candidates[ch] = true;
				}
			}
			return candidates;
		},

		_isLetterOrDigit: function(/*String*/ch) {
			return this.letterRegExp.test(ch) || this.digitRegExp.test(ch);
		},

		_intersect: function(/*Object*/set1, /*Object*/set2) {
			var result = {};
			for (var elem in set1) {
				if (set2[elem]) {
					result[elem] = true;
				}
			}
			return result;
		},

		validateForm: function(/*Object*/errorParams) {
			// summary: make Data value available to action controller
			var val = this.inherited(arguments);
			this.defView.m_formData.setFieldInitialValue("Data", this._reassembleData(this._dataRows));
			return val;
		},

		_reassembleData: function(rows) {
			// summary: pack the parsed data back into a string that's easy for the server to parse
			return _.map(rows, function(row) {return row.join("\t");}).join("\n");
		}
	});
});